import React from 'react';
import { Navigate, type RouteObject } from 'react-router-dom';

// Lazy import components for routing
const AdminLayout = React.lazy(() => import('@/features/admin/layouts/AdminLayout').then(m => ({ default: m.AdminLayout })));
const DashboardPage = React.lazy(() => import('@/features/dashboard/pages/DashboardPage').then(m => ({ default: m.DashboardPage })));
const UserManagementPage = React.lazy(() => import('@/features/user/pages/UserManagementPage').then(m => ({ default: m.UserManagementPage })));
const LanguageManagementPage = React.lazy(() => import('@/features/language/pages/LanguageManagementPage').then(m => ({ default: m.LanguageManagementPage })));
const VoiceManagementPage = React.lazy(() => import('@/features/voice/pages/VoiceManagementPage').then(m => ({ default: m.VoiceManagementPage })));
const ImageManagementPage = React.lazy(() => import('@/features/image/pages/ImageManagementPage').then(m => ({ default: m.ImageManagementPage })));
const AudioManagementPage = React.lazy(() => import('@/features/audio/pages/AudioManagementPage').then(m => ({ default: m.AudioManagementPage })));
const TranslationManagementPage = React.lazy(() => import('@/features/translation/pages/TranslationManagementPage').then(m => ({ default: m.TranslationManagementPage })));
const WordMediaManagementPage = React.lazy(() => import('@/features/word-media/pages/WordMediaManagementPage').then(m => ({ default: m.WordMediaManagementPage })));
const SentenceManagementPage = React.lazy(() => import('@/features/sentence/pages/SentenceManagementPage'));
const ParagraphManagementPage = React.lazy(() => import('@/features/paragraph/pages/ParagraphManagementPage'));
const PageMediaManagementPage = React.lazy(() => import('@/features/page-media/pages/PageMediaManagementPage'));
const StoryMediaManagementPage = React.lazy(() => import('@/features/story-media/pages/StoryMediaManagementPage'));
const WordMediaSetManagementPage = React.lazy(() => import('@/features/word-media-set/pages/WordMediaSetManagementPage'));
const SeedManagementPage = React.lazy(() => import('@/features/seed/pages/SeedManagementPage'));
const BatchCreateSeedPage = React.lazy(() => import('@/features/seed/pages/BatchCreateSeedPage'));
const SeedBatchManagementPage = React.lazy(() => import('@/features/seed-batch/pages/SeedBatchManagementPage'));
const CreateSeedBatchPage = React.lazy(() => import('@/features/seed-batch/pages/CreateSeedBatchPage'));
const EditSeedBatchPage = React.lazy(() => import('@/features/seed-batch/pages/EditSeedBatchPage'));
const AdvancedSeedBatchPage = React.lazy(() => import('@/features/seed-batch/pages/AdvancedSeedBatchPage'));
const GenerateAudioPage = React.lazy(() => import('@/features/seed-batch/pages/GenerateAudioPage').then(m => ({ default: m.GenerateAudioPage })));
const ImageConverterListPage = React.lazy(() => import('@/features/image-converter/pages/ImageConverterListPage'));
const ImageConverterDetailPage = React.lazy(() => import('@/features/image-converter/pages/ImageConverterDetailPage'));
const AudioConverterListPage = React.lazy(() => import('@/features/audio-converter/pages/AudioConverterListPage'));
const AudioConverterDetailPage = React.lazy(() => import('@/features/audio-converter/pages/AudioConverterDetailPage'));
const AzureTTSListPage = React.lazy(() => import('@/features/azure-tts/pages/AzureTTSListPage').then(m => ({ default: m.AzureTTSListPage })));
const AzureTTSDetailPage = React.lazy(() => import('@/features/azure-tts/pages/AzureTTSDetailPage'));
const GeminiTTSListPage = React.lazy(() => import('@/features/gemini-tts/pages/GeminiTTSListPage').then(m => ({ default: m.GeminiTTSListPage })));
const GeminiTTSDetailPage = React.lazy(() => import('@/features/gemini-tts/pages/GeminiTTSDetailPage'));

// Auth components
const AuthGuard = React.lazy(() => import('@/features/auth/components/AuthGuard').then(m => ({ default: m.AuthGuard })));
const GuestGuard = React.lazy(() => import('@/features/auth/components/GuestGuard').then(m => ({ default: m.GuestGuard })));
const LoginPage = React.lazy(() => import('@/features/auth/pages/LoginPage').then(m => ({ default: m.LoginPage })));
const RegisterPage = React.lazy(() => import('@/features/auth/pages/RegisterPage').then(m => ({ default: m.RegisterPage })));
const OTPPage = React.lazy(() => import('@/features/auth/pages/OTPPage').then(m => ({ default: m.OTPPage })));

export const routeDefinitions: RouteObject[] = [
  {
    path: '/',
    element: <Navigate to="/admin" replace />,
  },
  {
    path: '/admin',
    element: (
      <AuthGuard>
        <AdminLayout />
      </AuthGuard>
    ),
    children: [
      {
        index: true,
        element: <DashboardPage />,
      },
      {
        path: 'users',
        element: <UserManagementPage />,
      },
      {
        path: 'languages',
        element: <LanguageManagementPage />,
      },
      {
        path: 'voices',
        element: <VoiceManagementPage />,
      },
      {
        path: 'images',
        element: <ImageManagementPage />,
      },
      {
        path: 'audios',
        element: <AudioManagementPage />,
      },
      {
        path: 'translations',
        element: <TranslationManagementPage />,
      },
      {
        path: 'word-media',
        element: <WordMediaManagementPage />,
      },
      {
        path: 'sentences',
        element: <SentenceManagementPage />,
      },
      {
        path: 'paragraphs',
        element: <ParagraphManagementPage />,
      },
      {
        path: 'page-media',
        element: <PageMediaManagementPage />,
      },
      {
        path: 'story-media',
        element: <StoryMediaManagementPage />,
      },
      {
        path: 'word-media-sets',
        element: <WordMediaSetManagementPage />,
      },
      {
        path: 'seeds',
        element: <SeedManagementPage />,
      },
      {
        path: 'seeds/create',
        element: <BatchCreateSeedPage />,
      },
      {
        path: 'seed-batches',
        element: <SeedBatchManagementPage />,
      },
      {
        path: 'seed-batches/create',
        element: <CreateSeedBatchPage />,
      },
      {
        path: 'seed-batches/:id',
        element: <EditSeedBatchPage />,
      },
      {
        path: 'seed-batches/:id/add-advanced',
        element: <AdvancedSeedBatchPage />,
      },
      {
        path: 'seed-batches/:id/generate-audio',
        element: <GenerateAudioPage />,
      },
      {
        path: 'image-converter',
        element: <ImageConverterListPage />,
      },
      {
        path: 'image-converter/new',
        element: <ImageConverterDetailPage />,
      },
      {
        path: 'image-converter/:id',
        element: <ImageConverterDetailPage />,
      },
      {
        path: 'audio-converter',
        element: <AudioConverterListPage />,
      },
      {
        path: 'audio-converter/new',
        element: <AudioConverterDetailPage />,
      },
      {
        path: 'audio-converter/:id',
        element: <AudioConverterDetailPage />,
      },
      {
        path: 'azure-tts',
        element: <AzureTTSListPage />,
      },
      {
        path: 'azure-tts/:id',
        element: <AzureTTSDetailPage />,
      },
      {
        path: 'gemini-tts',
        element: <GeminiTTSListPage />,
      },
      {
        path: 'gemini-tts/:id',
        element: <GeminiTTSDetailPage />,
      },
    ],
  },
  {
    path: '/auth',
    children: [
      {
        index: true,
        element: <Navigate to="/auth/login" replace />,
      },
      {
        path: 'login',
        element: (
          <GuestGuard>
            <LoginPage />
          </GuestGuard>
        ),
      },
      {
        path: 'register',
        element: (
          <GuestGuard>
            <RegisterPage />
          </GuestGuard>
        ),
      },
      {
        path: 'otp',
        element: (
          <GuestGuard>
            <OTPPage />
          </GuestGuard>
        ),
      },
    ],
  },
  {
    path: '*',
    element: <Navigate to="/admin" replace />,
  },
];